import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { Sidebar } from "../components/Sidebar";
import { useAuth } from "../auth/useAuth";
import { byPath } from "./navigation";
import { usePageTitle } from "./usePageTitle";

function screenName(pathname: string): string {
  const segment = pathname.replace("/dashboard/", "").split("/")[0];
  if (segment === "unauthorized") return "Not authorized";
  return byPath.get(segment)?.label ?? "Admin";
}

/** The persistent chrome around every dashboard screen. */
export function AppShell() {
  const { principal, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const screen = screenName(location.pathname);
  usePageTitle(screen);

  const email = principal?.email ?? "";
  const initial = email.trim().charAt(0).toUpperCase() || "?";

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <AppBar position="sticky" color="inherit" elevation={1}>
          <Toolbar>
            <Typography variant="h6" component="h1" sx={{ flexGrow: 1 }}>
              {screen}
            </Typography>
            <Button
              onClick={(event) => setAnchor(event.currentTarget)}
              endIcon={<ExpandMoreIcon />}
              aria-haspopup="menu"
              aria-label={`Account menu for ${email}`}
              sx={{ textTransform: "none" }}
            >
              <Avatar sx={{ width: 28, height: 28, mr: 1, fontSize: 14 }}>{initial}</Avatar>
              <Box
                component="span"
                sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {email}
              </Box>
            </Button>
            <Menu open={anchor !== null} anchorEl={anchor} onClose={() => setAnchor(null)}>
              <MenuItem
                onClick={() => {
                  setAnchor(null);
                  navigate("/dashboard/profile");
                }}
              >
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                Profile
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  setAnchor(null);
                  signOut();
                  navigate("/login", { replace: true });
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Log out
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        <Box component="main" sx={{ p: 3 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
